import { useMemo, useRef, useState, type MouseEvent } from 'react';
import { useAppSelector } from '../../../Redux/hooks';
import { selectAbsencesByStudent, selectSlotsByStudent } from '../../../Redux/selectors';
import { useTranslation } from '../../../i18n/useTranslation';
import { WEEKDAY_KEYS } from '../../../i18n/weekdays';
import type { LessonModel } from '../../../Models/LessonModel';
import type { StudentAbsenceModel, StudentSlotModel } from '../../../Models/StudentModel';
import { LessonStatus, type Weekday } from '../../../Models/enums';
import { formatDate, formatMonth } from '../../../Utils/DateUtil';
import { isCancelled, lessonVisualState, localDateString } from '../../../Utils/LessonStatusUtil';
import { plannedLessonsFor } from '../../../Utils/PlannedLessonUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { LessonStatusSheet } from '../Lessons/LessonStatusSheet';
import { QuickAddLesson } from '../Lessons/QuickAddLesson';
import { WeekView, type PlannedLesson, type WeekDayModel } from './WeekView';
import './Calendar.css';
import { useDisplayName } from '../../../Utils/useDisplayName';

type CalendarMode = 'month' | 'week' | 'day';

export function Calendar() {
  const { tr, language, direction } = useTranslation();
  const lessons = useAppSelector(state => state.lessons.items);
  const state = useAppSelector(store => store.lessons.state);
  const error = useAppSelector(store => store.lessons.error);
  const slotsByStudent = useAppSelector(selectSlotsByStudent);
  const absencesByStudent = useAppSelector(selectAbsencesByStudent);
  const [focusDate, setFocusDate] = useState(localDateString);
  const [mode, setMode] = useState<CalendarMode>('month');
  const [open, setOpen] = useState<LessonModel | null>(null);
  const [plannedAdd, setPlannedAdd] = useState<PlannedLesson | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const navTokenRef = useRef(0);

  /**
   * Every lesson by day, cancelled ones included. The calendar draws only the
   * appointments (`shownOn`), but a cancelled lesson still books its hour:
   * the tutor called that hour off, so the recurring placeholder must not
   * come back in its place offering to log it again.
   */
  const byDate = useMemo(() => groupLessons(lessons), [lessons]);
  const shownOn = (date: string) => (byDate.get(date) ?? []).filter(lesson => !isCancelled(lesson));
  const monthDays = useMemo(() => buildMonth(focusDate.slice(0, 7)), [focusDate]);
  const weekDays = useMemo(() => buildWeek(focusDate), [focusDate]);
  const today = localDateString();

  /**
   * Everything the week view draws, resolved here rather than there: the
   * slot lookups and the status vocabulary already live on this screen, and
   * WeekView renders two different layouts from the same model. Keeping the
   * derivation in one memo means neither layout can drift from the other.
   */
  const weekModel = useMemo<WeekDayModel[]>(() => weekDays.map((day, index) => {
    const dayLessons = byDate.get(day.key) ?? [];
    const slotsFor = (student: string) => slotsByStudent.get(student) ?? [];
    return {
      key: day.key,
      number: day.number,
      weekdayLabel: tr(WEEKDAY_KEYS[index as Weekday]).slice(0, 3),
      isToday: day.key === today,
      lessons: shownOn(day.key).map(lesson => {
        const visual = lessonVisualState(lesson, slotsFor(lesson.student));
        return {
          lesson,
          visual,
          statusLabel: tr(statusKey(lesson, visual)),
          durationMinutes: slotsFor(lesson.student).find(slot => slot.time === lesson.time)?.durationMinutes ?? 45
        };
      }),
      planned: plannedLessonsFor(day.key, slotsByStudent, absencesByStudent, dayLessons)
    };
  }), [weekDays, byDate, slotsByStudent, absencesByStudent, today, tr]);

  if (state === 'loading' || state === 'idle') return <Spinner />;
  if (state === 'failed') return <ErrorState code={error} />;

  // A directional cross-fade for period navigation: new content enters from
  // the side its chevron points to, old content exits the other way (see
  // .cal-nav-btn's RTL/LTR-flipped svg). A click carries no gesture velocity
  // to hand off, so this is a plain CSS transition rather than a spring —
  // only the direction and the presentation-value handling carry over.
  //
  // Only one .cal-panel is ever mounted (month/week/day render conditionally,
  // not as hidden siblings), so the "outgoing" layer is a clone of whatever
  // is there right before `mutate` changes it, not a lookup by mode.
  function animatedShift(dir: -1 | 0 | 1, mutate: () => void) {
    const viewport = viewportRef.current;
    const live = viewport?.querySelector<HTMLElement>(':scope > .cal-panel');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!viewport || !live || reduceMotion || !dir) {
      mutate();
      return;
    }

    const token = ++navTokenRef.current;
    const isRtl = document.documentElement.dir !== 'ltr';
    const enterSign = isRtl ? -dir : dir;
    const SLIDE_PX = 28;

    const outgoing = live.cloneNode(true) as HTMLElement;
    outgoing.removeAttribute('id');
    outgoing.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
    outgoing.classList.add('cal-panel-outgoing');
    viewport.appendChild(outgoing);

    live.style.transition = 'none';
    live.style.transform = `translateX(${enterSign * SLIDE_PX}px)`;
    live.style.opacity = '0';

    mutate(); // schedules the re-render; React patches `live`'s children in place and leaves its inline style alone

    // Two frames: the first lets React commit the new content under the
    // start-state styles above, the second starts the transition from a
    // state the browser has actually painted once.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (token !== navTokenRef.current) { outgoing.remove(); return; } // superseded by a newer nav
      const transition = 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1), opacity 200ms ease-out';
      outgoing.style.transition = transition;
      outgoing.style.transform = `translateX(${-enterSign * SLIDE_PX}px)`;
      outgoing.style.opacity = '0';
      live.style.transition = transition;
      live.style.transform = 'translateX(0)';
      live.style.opacity = '1';
      const cleanup = () => {
        outgoing.remove();
        live.style.transition = '';
        live.style.transform = '';
        live.style.opacity = '';
      };
      live.addEventListener('transitionend', cleanup, { once: true });
      setTimeout(cleanup, 320); // fallback if transitionend never fires (e.g. tab backgrounded)
    }));
  }

  function move(delta: -1 | 1) {
    animatedShift(delta, () => {
      setFocusDate(current => shiftDate(current, mode === 'month' ? { months: delta } : { days: delta * (mode === 'week' ? 7 : 1) }));
    });
  }

  function goToday() {
    const dir = today > focusDate ? 1 : today < focusDate ? -1 : 0;
    animatedShift(dir, () => setFocusDate(today));
  }

  function openDay(date: string) {
    setFocusDate(date);
    setMode('day');
  }

  const title = calendarTitle(mode, focusDate, weekDays, language);

  return (
    <section className="calendar" dir={direction}>
      <section className="calendar-card">
        <header className="cal-toolbar">
          <div className="cal-titlebar">
            <h1 className="cal-title"><bdi>{title}</bdi></h1>
            <p className="cal-subtitle">{tr(mode === 'month' ? 'calendarMonth' : mode === 'week' ? 'calendarWeek' : 'calendarDay')}</p>
          </div>

          <div className="cal-controls">
            <div className="cal-nav" role="group" aria-label={tr('navCalendar')}>
              <button type="button" className="cal-nav-btn is-previous" onClick={() => move(-1)} aria-label={tr('calendarPrevious')}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
              </button>
              <button type="button" className="cal-today-btn" onClick={goToday}>{tr('calendarToday')}</button>
              <button type="button" className="cal-nav-btn is-next" onClick={() => move(1)} aria-label={tr('calendarNext')}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m15 6-6 6 6 6" /></svg>
              </button>
            </div>

            <div className="calendar-mode-switch" role="group" aria-label={tr('calendarView')}>
              {(['month', 'week', 'day'] as CalendarMode[]).map(option => (
                <button
                  key={option}
                  type="button"
                  className={mode === option ? 'calendar-mode-btn is-active' : 'calendar-mode-btn'}
                  onClick={() => setMode(option)}
                  aria-pressed={mode === option}
                >
                  {tr(option === 'month' ? 'calendarMonth' : option === 'week' ? 'calendarWeek' : 'calendarDay')}
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className="cal-legend" aria-label={tr('calendarView')}>
          <Legend state="is-paid" label={tr('paid')} />
          <Legend state="is-unpaid" label={tr('unpaid')} />
          <Legend state="is-scheduled" label={tr('scheduled')} />
          <Legend state="is-planned" label={tr('calendarPlanned')} />
        </div>

        <div className="cal-viewport" ref={viewportRef}>
          {mode === 'month' && (
            <div className="cal-panel cal-month-panel">
              <div className="calendar-weekdays" role="row">
                {weekdays(tr).map(day => <div key={day} className="calendar-weekday" role="columnheader">{day}</div>)}
              </div>
              <div className="calendar-grid" role="grid">
                {monthDays.map(day => {
                  const dayLessons = shownOn(day.key);
                  const planned = plannedLessonsFor(day.key, slotsByStudent, absencesByStudent, byDate.get(day.key) ?? []);
                  const total = dayLessons.length + planned.length;
                  return (
                    <div
                      key={day.key}
                      className={`calendar-cell${day.inMonth ? '' : ' is-outside'}${day.key === today ? ' is-today' : ''}${total ? ' has-lessons' : ''}`}
                      role="gridcell"
                      onClick={() => openDay(day.key)}
                    >
                      <div className="calendar-cell-head">
                        <span className="calendar-day-num"><bdi>{day.number}</bdi></span>
                        {total > 0 && <span className="calendar-cell-count"><bdi>{total}</bdi></span>}
                      </div>
                      <div className="calendar-chips">
                        {dayLessons.slice(0, 3).map(lesson => (
                          <LessonChip key={lesson.id} lesson={lesson} state={lessonVisualState(lesson, slotsByStudent.get(lesson.student) ?? [])} onOpen={setOpen} />
                        ))}
                        {dayLessons.length < 3 && planned.slice(0, 3 - dayLessons.length).map(item => <PlannedChip key={`${item.student}-${item.time}`} item={item} onAdd={setPlannedAdd} />)}
                        {total > 3 && <span className="cal-chip cal-chip-more"><bdi>+{total - 3}</bdi></span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {mode === 'week' && <WeekView days={weekModel} onOpen={setOpen} onOpenDay={openDay} onAdd={setPlannedAdd} tr={tr} />}

          {mode === 'day' && (
            <DayAgenda
              date={focusDate}
              lessons={byDate.get(focusDate) ?? []}
              slotsByStudent={slotsByStudent}
              absencesByStudent={absencesByStudent}
              onOpen={setOpen}
              onAdd={setPlannedAdd}
              empty={tr('calendarNoDayLessons')}
              language={language}
              tr={tr}
            />
          )}
        </div>
      </section>

      {open && <LessonStatusSheet lesson={open} onClose={() => setOpen(null)} />}
      {plannedAdd && <QuickAddLesson onClose={() => setPlannedAdd(null)} initialStudentName={plannedAdd.student} initialDate={plannedAdd.date} initialTime={plannedAdd.time} />}
    </section>
  );
}

function PlannedChip({ item, onAdd }: { item: PlannedLesson; onAdd: (item: PlannedLesson) => void }) {
  const displayName = useDisplayName();
  return (
    <button type="button" className="cal-chip is-planned" onClick={event => { event.stopPropagation(); onAdd(item); }}>
      <bdi className="cal-chip-time">{item.time}</bdi>
      <span className="cal-chip-name">{displayName(item.student)}</span>
    </button>
  );
}

function Legend({ state, label }: { state: string; label: string }) {
  return <span className="cal-legend-item"><i className={`cal-legend-swatch ${state}`} aria-hidden="true" />{label}</span>;
}

function LessonChip({ lesson, state, onOpen }: { lesson: LessonModel; state: string; onOpen: (lesson: LessonModel) => void }) {
  const displayName = useDisplayName();
  function click(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onOpen(lesson);
  }
  return (
    <button type="button" className={`cal-chip ${state}`} onClick={click}>
      {lesson.time && <bdi className="cal-chip-time">{lesson.time}</bdi>}
      <span className="cal-chip-name">{displayName(lesson.student)}</span>
    </button>
  );
}

function DayAgenda({ date, lessons, slotsByStudent, absencesByStudent, onOpen, onAdd, empty, language, tr }: {
  date: string;
  lessons: LessonModel[];
  slotsByStudent: Map<string, StudentSlotModel[]>;
  absencesByStudent: Map<string, StudentAbsenceModel[]>;
  onOpen: (lesson: LessonModel) => void;
  onAdd: (item: PlannedLesson) => void;
  empty: string;
  language: 'he' | 'en';
  tr: ReturnType<typeof useTranslation>['tr'];
}) {
  const displayName = useDisplayName();
  const planned = plannedLessonsFor(date, slotsByStudent, absencesByStudent, lessons);
  const shown = lessons.filter(lesson => !isCancelled(lesson));
  return (
    <div className="cal-panel calendar-day-view">
      <h2 className="cal-day-heading"><bdi>{formatDate(date, language)}</bdi></h2>
      {shown.length === 0 && planned.length === 0
        ? <p className="cal-empty">{empty}</p>
        : (
          <ul className="cal-agenda">
            {shown.map(lesson => {
              const visual = lessonVisualState(lesson, slotsByStudent.get(lesson.student) ?? []);
              return (
                <li key={lesson.id}>
                  <button type="button" className={`cal-agenda-row ${visual}`} onClick={() => onOpen(lesson)}>
                    <bdi className="cal-agenda-time">{lesson.time ?? tr('calendarNoTime')}</bdi>
                    <span className="cal-agenda-name">{displayName(lesson.student)}</span>
                    <MoneyText amount={lesson.amount} className="cal-agenda-amount" />
                    <span className="cal-agenda-status">{tr(statusKey(lesson, visual))}</span>
                  </button>
                </li>
              );
            })}
            {planned.map(item => (
              <li key={`${item.student}-${item.time}`}>
                <button type="button" className="cal-agenda-row is-planned" onClick={() => onAdd(item)}>
                  <bdi className="cal-agenda-time">{item.time}</bdi>
                  <span className="cal-agenda-name">{displayName(item.student)}</span>
                  <span className="cal-agenda-amount" />
                  <span className="cal-agenda-status">{tr('calendarPlanned')}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

interface Cell { key: string; number: number; inMonth: boolean }

function groupLessons(lessons: LessonModel[]) {
  const map = new Map<string, LessonModel[]>();
  for (const lesson of lessons) {
    const group = map.get(lesson.date);
    if (group) group.push(lesson);
    else map.set(lesson.date, [lesson]);
  }
  for (const group of map.values()) group.sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''));
  return map;
}

function weekdays(tr: ReturnType<typeof useTranslation>['tr']) {
  return ([0, 1, 2, 3, 4, 5, 6] as Weekday[]).map(day => tr(WEEKDAY_KEYS[day]).slice(0, 3));
}

function buildMonth(month: string): Cell[] {
  const [year, monthNumber] = month.split('-').map(Number);
  const first = new Date(year ?? 1970, (monthNumber ?? 1) - 1, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const at = new Date(start);
    at.setDate(start.getDate() + index);
    return { key: localDateString(at), number: at.getDate(), inMonth: at.getMonth() === first.getMonth() };
  });
}

function buildWeek(date: string): Cell[] {
  const at = localDate(date);
  at.setDate(at.getDate() - at.getDay());
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(at);
    day.setDate(at.getDate() + index);
    return { key: localDateString(day), number: day.getDate(), inMonth: true };
  });
}

function calendarTitle(mode: CalendarMode, focus: string, week: Cell[], language: 'he' | 'en') {
  if (mode === 'month') return formatMonth(focus, language);
  if (mode === 'day') return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(localDate(focus));
  return `${formatDate(week[0]?.key ?? focus, language)} – ${formatDate(week[6]?.key ?? focus, language)}`;
}

function shiftDate(value: string, delta: { months?: number; days?: number }) {
  const date = localDate(value);
  if (delta.months) date.setMonth(date.getMonth() + delta.months, 1);
  if (delta.days) date.setDate(date.getDate() + delta.days);
  return localDateString(date);
}

function localDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

function statusKey(lesson: LessonModel, visual: string) {
  if (visual === 'is-scheduled') return 'scheduled' as const;
  if (lesson.status === LessonStatus.Paid) return 'paid' as const;
  if (lesson.status === LessonStatus.CancelledCharged) return 'stCancelledCharged' as const;
  if (lesson.status === LessonStatus.CancelledFree) return 'stCancelledFree' as const;
  return 'unpaid' as const;
}
